import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { InputType } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import countries from '../../../countries.json';
import { FormGroup } from '@angular/forms';
import { UtilsService } from 'src/app/services';
import { TooltipPosition } from '../tooltip/tooltip-position';
import moment from 'moment';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from '../tooltip/tooltip.module';
import { Select2 } from 'ng-select2-component';
import { CalendarComponent } from './calendar/calendar.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { ColorComponent } from './color/color.component';
import { ConvertibleSeatsInputComponent } from './convertible-seats-input/convertible-seats-input.component';
import { MultiSelectFormComponent } from './multi-select-group/multi-select-form/multi-select-form.component';
import { MultiselectCheckboxesComponent } from './multiselect-checkboxes/multiselect-checkboxes.component';
import { SeatsInputComponent } from './seats-input/seats-input.component';
import { SelectComponent } from './select/select.component';
import { SelectWithEnumComponent } from './select-with-enum/select-with-enum.component';
import { Select2OptionComponent } from '../select2-option/select2-option.component';
import { ToggleComponent } from './toggle/toggle.component';
@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    Select2,
    CalendarComponent,
    CheckboxComponent,
    ColorComponent,
    ConvertibleSeatsInputComponent,
    MultiSelectFormComponent,
    MultiselectCheckboxesComponent,
    SeatsInputComponent,
    SelectComponent,
    SelectWithEnumComponent,
    Select2OptionComponent,
    ToggleComponent,
  ],
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InputComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() data: any;
  @Input() edit?: boolean;
  @Input() independent?: boolean;
  @Input() translateValue?: boolean = false;
  @Input() hasErrors?: boolean = false;
  @Input() error?: string = '';
  @Input() formGroup?: FormGroup;
  @Output() changeEventEmitter = new EventEmitter<any>();
  @Output() inputEventEmitter = new EventEmitter<any>();
  @Output() setEventEmitter = new EventEmitter<any>();
  @Output() scrollEventEmitter = new EventEmitter<any>();
  @Output() searchEventEmitter = new EventEmitter<any>();

  public InputType = InputType;
  public TooltipPosition = TooltipPosition;
  public countries: any[] = [];
  public dialCodes: any[] = [];
  public images = images;
  public dialCodeSelected = '+34';
  public dialCodeCountry = 'ES';

  public autocompleteOptions: any[] = [];

  public timeout = setTimeout(() => {}, 0);

  public searching = false;

  public disabledKeys = ['ArrowDown', 'ArrowUp', 'Backspace', 'Enter'];

  @HostListener('keydown', ['$event'])
  onKeyDown(e: any) {
    if (e.key && this.disabledKeys.includes(e.key)) {
      switch (e.key) {
        case 'Backspace':
          if (this.data.edit.inputType !== InputType.AUTOCOMPLETE_SEARCH) {
            this.searching = e.target.value !== '';
          }
          if (e.target.value === '') e.preventDefault();
          break;
        case 'Enter':
          if (this.data.edit.inputType === InputType.AUTOCOMPLETE_SEARCH) {
            this.searching = true;
            this.searchEventEmitter.emit({ search: e.target.value });
            setTimeout(() => {
              if (this.searching) this.searching = false;
            }, 1500);
          }
          break;
        default:
          this.searching = false;
          break;
      }
    } else {
      if (this.data.edit.inputType !== InputType.AUTOCOMPLETE_SEARCH)
        this.searching = true;
    }
  }

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private cdr: ChangeDetectorRef,
  ) {
    countries.map((country: any) => {
      const element = {
        value: country.dial_code,
        label: `${country.name} ${country.dial_code}`,
        data: { image: country.emoji, name: country.name },
      };
      this.countries.push(element);
      this.dialCodes.push(country.dial_code);
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    if (this.data?.edit?.inputType === InputType.PHONE) {
      this.dialCodeSelected =
        this.dialCodes.find((option: string) => {
          return this.data.value && this.data.value.includes(option, 0);
        }) || '+34';
      this.data.value = this.data.value?.replace(this.dialCodeSelected, '');
      this.dialCodeCountry =
        countries.find(
          (country: any) => country.dial_code === this.dialCodeSelected,
        )?.dial_code || '+34';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      if (
        this.data.title === 'bookings.data.originStop' ||
        this.data.title === 'bookings.data.destinationStop'
      ) {
        this.autocompleteOptions = this.data.edit.values;
        if (this.searching && this.data.edit.values.length > 0)
          this.searching = false;
      }
    }
    this.cdr.detectChanges();
  }

  change(event: any) {
    this.changeEventEmitter.emit(event);
  }

  onSet(event: any) {
    if (!this.data.value || this.data.value?.name !== event.value.name)
      this.setEventEmitter.emit(event);
  }

  onPhonePrefixChange(event: any) {
    this.dialCodeSelected = event.value;
    this.changeEventEmitter.emit({
      target: { tagName: 'SELECT', value: event.value },
    });
  }

  cancelEvent(event: any) {
    if (!this.utilsService.isTouchDevice()) event.preventDefault();
  }

  searchSelect2(event: any) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.searchEventEmitter.emit(event), 500);
  }

  onOpen() {
    if (this.data.edit?.inputType === InputType.AUTOCOMPLETE_SEARCH) {
      setTimeout(() => {
        const select2Element = document.getElementById(
          `${this.data.title}-search-field`,
        ) as HTMLInputElement;
        if (select2Element) {
          if (this.data.value) {
            select2Element.value = this.data.value.name;
          } else {
            select2Element.value = select2Element.value ?? '';
            select2Element?.setAttribute(
              'placeholder',
              this.data.searchPlaceholder,
            );
          }
          select2Element.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 0);
    }
  }

  onClose() {
    this.searching = false;
  }

  onSearch() {
    if (this.data.edit?.inputType === InputType.AUTOCOMPLETE_SEARCH) {
      this.autocompleteOptions = [];
    }
  }

  getAutoCompletePlaceholder() {
    if (this.data.value) return this.data.value.name;
    return this.data.placeholder;
  }

  isOptionChecked(value: any) {
    if (!value || !this.data?.value) return false;
    return this.data.value.some((val: any) => val?.id === value?.id);
  }

  selectionOverride() {
    if (this.data.edit.inputType === InputType.MULTISELECT_SCROLL) {
      if (
        this.data.value.some(
          (value: any) => value.dni || value.phone || value.email,
        )
      ) {
        return this.data && this.data.value.length > 0
          ? this.data.value
              .map((value: any) => this.utilsService.getUserLabel(value))
              .join(', ')
          : '';
      } else if (this.data.value.some((value: any) => value.dateTime)) {
        return this.data && this.data.value.length > 0
          ? this.data.value
              .map(
                (value: any) =>
                  `${moment(value.dateTime).format('ddd DD/MM')} - ${moment(value.dateTime).format('HH:mm')}`,
              )
              .join(', ')
          : '';
      } else {
        return this.data && this.data.value.length > 0
          ? this.data.value
              .map((value: any) => value.tag || value.name)
              .join(', ')
          : '';
      }
    }
  }
}
